// Stripe's Payment Element draws its labels and fields in one of Stripe's themes. Pages follow the page theme
// (html.dark). A checkout with the builder style guide sheet (data-stripe-style-guide-colors) uses the customer's
// colors instead, and those are often light while the form is in dark mode: the night theme then put white labels
// on a white page. An inline form's iframe is transparent, so the page color it sits on can't be read from here.
// The style guide's paragraph color is picked to read on that page, so dark paragraph text means a light page.
export function stripeTheme(
  target,
  { root = document.documentElement, getStyle = getComputedStyle } = {}
) {
  // A page that stays light in dark mode (the trial signup) marks its form so the labels don't go white on white.
  if (target?.closest?.('[data-stripe-theme="light"]')) return 'stripe'

  const container = target?.closest?.('[data-stripe-style-guide-colors]')
  const text = container ? paragraphColor(container, getStyle) : null
  const dark = text ? !isDarkColor(text) : root.classList.contains('dark')

  return dark ? 'night' : 'stripe'
}

// Null when the style guide sets no paragraph color.
function paragraphColor(container, getStyle) {
  const probe = container.ownerDocument.createElement('span')
  probe.hidden = true
  probe.style.color = 'var(--color-paragraph, transparent)'
  container.appendChild(probe)

  try {
    const color = parseRgb(getStyle(probe).color)
    return color && color.alpha > 0 ? color : null
  } finally {
    probe.remove()
  }
}

export function parseRgb(value) {
  const match =
    /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:[\s,/]+([\d.]+%?))?\s*\)$/.exec(
      value || ''
    )
  if (!match) return null

  const [, red, green, blue, alpha = '1'] = match
  return {
    red: Number(red),
    green: Number(green),
    blue: Number(blue),
    alpha: alpha.endsWith('%')
      ? Number(alpha.slice(0, -1)) / 100
      : Number(alpha),
  }
}

// Below 0.179 relative luminance white text has more contrast than black.
export function isDarkColor({ red, green, blue }) {
  const channel = (value) => {
    const c = value / 255
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  }
  const luminance =
    0.2126 * channel(red) + 0.7152 * channel(green) + 0.0722 * channel(blue)

  return luminance < 0.179
}
